import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PageHeader, Panel } from "@/components/AppShell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Field } from "@/pages/Profile";
import { usePortfolio } from "@/context/ProfileContext";
import { api, errMsg, useFetch } from "@/lib/api";
import { growTo, iraLimit, rmdSchedule, rmdStartAge, LIMITS } from "@/lib/finance";
import { usd, compact } from "@/lib/format";

function ContributionLog({ account }) {
  const year = new Date().getFullYear();
  const { data, reload } = useFetch(`/ira/contributions?year=${year}`);
  const [amt, setAmt] = useState("");
  const add = async () => {
    try {
      await api.post("/ira/contributions", { account, amount: Number(amt), year });
      setAmt("");
      toast.success("Contribution logged");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { await api.delete(`/ira/contributions/${id}`); reload(); };
  const used = data?.used || 0, limit = data?.limit || LIMITS.ira;
  return (
    <div className="space-y-3" data-testid={`contrib-log-${account}`}>
      <div className="flex justify-between text-xs"><span className="text-muted-foreground">{year} combined IRA contributions</span><span className="font-mono-num" data-testid={`contrib-used-${account}`}>{usd(used)} / {usd(limit)}</span></div>
      <Progress value={(used / limit) * 100} />
      <div className="flex gap-2">
        <Input type="number" className="font-mono-num" placeholder="Amount" value={amt} onChange={(e) => setAmt(e.target.value)} data-testid={`contrib-amount-${account}`} />
        <Button onClick={add} disabled={!amt} data-testid={`contrib-add-${account}`}>Log</Button>
      </div>
      <ul className="text-sm divide-y divide-border/60">
        {data?.items?.filter((i) => i.account === account).map((i) => (
          <li key={i.id} className="flex justify-between items-center py-1.5"><span className="font-mono-num">{usd(i.amount, 2)}</span><span className="text-xs text-muted-foreground">{i.created_at?.slice(0, 10)}</span><Button variant="ghost" size="icon" onClick={() => del(i.id)} data-testid={`contrib-del-${i.id}`}><Trash size={14} /></Button></li>
        ))}
      </ul>
    </div>
  );
}

function IraTab({ kind, p, setP, balance }) {
  const annualKey = kind === "trad" ? "ira_trad_annual" : "ira_roth_annual";
  const rate = Number(p.ira_return ?? 7);
  const years = Math.max(0, p.retire_age - p.age);
  const annual = Math.min(Number(p[annualKey]) || 0, iraLimit(p.age));
  const atRetire = growTo(balance, annual, rate, years);
  const birth = p.birth_year || new Date().getFullYear() - p.age;
  const rmdAge = rmdStartAge(birth);
  const atRmd = growTo(atRetire, 0, rate, Math.max(0, rmdAge - p.retire_age));
  const rmds = useMemo(() => rmdSchedule(atRmd, rmdAge, p.life_expectancy, rate), [atRmd, rmdAge, p.life_expectancy, rate]);
  const yrsDraw = Math.max(1, p.life_expectancy - p.retire_age);
  const r = rate / 100;
  const annuityPmt = r ? (atRetire * r) / (1 - Math.pow(1 + r, -yrsDraw)) : atRetire / yrsDraw;
  const taxEquiv = annuityPmt / (1 - (p.tax_rate || 0) / 100);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Panel title={kind === "trad" ? "Traditional IRA (pre-tax)" : "Roth IRA (post-tax)"} testid={`panel-${kind}-summary`}>
        <p className="text-xs text-muted-foreground">Current balance</p>
        <p className="font-mono-num text-2xl font-semibold mt-1" data-testid={`${kind}-balance`}>{usd(balance)}</p>
        <div className="mt-5 space-y-4">
          <Field label="Planned annual contribution" k={annualKey} p={p} set={setP} hint={`${LIMITS.year} limit ${usd(iraLimit(p.age))}${p.age >= 50 ? " incl. $1,100 catch-up" : ""}, shared across all IRAs`} />
          {(Number(p[annualKey]) || 0) > iraLimit(p.age) && <p className="text-xs text-rose-600" data-testid={`${kind}-over-limit`}>Capped at the IRS limit for projections.</p>}
          <Field label="Assumed annual return %" k="ira_return" p={{ ...p, ira_return: rate }} set={setP} />
        </div>
        <div className="mt-6 rounded-md bg-slate-900/[0.03] dark:bg-white/5 p-4">
          <p className="text-xs text-muted-foreground">Projected at {p.retire_age}</p>
          <p className="font-mono-num text-2xl font-semibold text-grad-money" data-testid={`${kind}-projected`}>{usd(atRetire)}</p>
        </div>
      </Panel>
      {kind === "trad" ? (
        <Panel title={`Required minimum distributions (start age ${rmdAge})`} className="lg:col-span-2" testid="panel-rmd">
          <div className="h-56">
            <ResponsiveContainer><BarChart data={rmds}><CartesianGrid vertical={false} strokeOpacity={0.3} /><XAxis dataKey="age" tick={{ fontSize: 11 }} /><YAxis tickFormatter={compact} tick={{ fontSize: 11 }} width={60} /><Tooltip formatter={(v) => usd(v)} /><Bar dataKey="rmd" name="RMD" fill="#0891B2" radius={[3, 3, 0, 0]} /></BarChart></ResponsiveContainer>
          </div>
          <div className="max-h-64 overflow-auto mt-4">
            <table className="w-full text-sm" data-testid="rmd-table">
              <thead className="text-xs text-muted-foreground"><tr><th className="text-left py-1">Age</th><th className="text-right">Start balance</th><th className="text-right">Divisor</th><th className="text-right">RMD</th><th className="text-right">Est. tax</th></tr></thead>
              <tbody>{rmds.map((x) => <tr key={x.age} className="border-t border-border/60 font-mono-num"><td className="py-1">{x.age}</td><td className="text-right">{usd(x.balance)}</td><td className="text-right">{x.divisor}</td><td className="text-right font-medium">{usd(x.rmd)}</td><td className="text-right text-rose-600">{usd(x.rmd * (p.tax_rate / 100))}</td></tr>)}</tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-3">SECURE 2.0: RMDs start at 73 if born 1951-1959 and 75 if born 1960 or later. Uses the IRS Uniform Lifetime Table.</p>
        </Panel>
      ) : (
        <Panel title="Tax-free distribution calculator" className="lg:col-span-2" testid="panel-roth-dist">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="glass p-4"><p className="text-xs text-muted-foreground">Tax-free income / yr ({yrsDraw} yrs)</p><p className="font-mono-num text-xl font-semibold mt-1 text-emerald-600 dark:text-emerald-400" data-testid="roth-annual-income">{usd(annuityPmt)}</p></div>
            <div className="glass p-4"><p className="text-xs text-muted-foreground">Monthly</p><p className="font-mono-num text-xl font-semibold mt-1" data-testid="roth-monthly-income">{usd(annuityPmt / 12)}</p></div>
            <div className="glass p-4"><p className="text-xs text-muted-foreground">Pre-tax equivalent at {p.tax_rate}%</p><p className="font-mono-num text-xl font-semibold mt-1" data-testid="roth-pretax-equiv">{usd(taxEquiv)}</p></div>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-5 leading-relaxed">Qualified Roth IRA withdrawals (age 59½ and account open 5+ years) are federal tax-free and have no lifetime RMDs for the original owner. To pull the same spendable income from a Traditional account you would need about {usd(taxEquiv - annuityPmt)} more per year before tax.</p>
        </Panel>
      )}
      <Panel title="Contribution log & IRS cap" testid={`panel-${kind}-log`}><ContributionLog account={kind === "trad" ? "trad_ira" : "roth_ira"} /></Panel>
    </div>
  );
}

export default function IRA() {
  const { profile, saveProfile, dash } = usePortfolio();
  const [p, setP] = useState(null);
  useEffect(() => { if (profile) setP(profile); }, [profile]);
  if (!p) return <p className="text-sm text-muted-foreground">Loading...</p>;
  const save = async () => { try { const { ira_return, ...rest } = p; await saveProfile(rest); toast.success("Saved"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <PageHeader title="IRA Center" subtitle="Traditional and Roth IRAs run in parallel with your TSP, with IRS limit caps, 50+ catch-up and RMD projections."
        actions={<Button className="rounded-full" onClick={save} data-testid="ira-save-button">Save plan</Button>} />
      <Tabs defaultValue="trad">
        <TabsList className="mb-6"><TabsTrigger value="trad" data-testid="ira-tab-trad">Traditional IRA</TabsTrigger><TabsTrigger value="roth" data-testid="ira-tab-roth">Roth IRA</TabsTrigger></TabsList>
        <TabsContent value="trad"><IraTab kind="trad" p={p} setP={setP} balance={dash?.totals?.trad_ira || 0} /></TabsContent>
        <TabsContent value="roth"><IraTab kind="roth" p={p} setP={setP} balance={dash?.totals?.roth_ira || 0} /></TabsContent>
      </Tabs>
    </div>
  );
}
